import React from "react";
import { Pressable, View } from "react-native";
import {
  InfoTooltipButton,
  InputPatientDetail,
  InputPatientDetailColorPicker,
  InputPatientDetailDropdown,
  NormalText,
  PrimaryButton,
  RainbowBorder,
  SecondaryButton,
} from "../../../common/native";
import { TDeployWidgetOnboardingStepId } from "./deployWidgetOnboardingContent";
import { SessionContext } from "../../../../contexts";
import { postClinicProfile } from "../../../../services/api-clinic";
import { CaretDown } from "../../../../assets";
import { DEPLOY_WIDGET_TOOLTIPS } from "./deployWidgetTooltips";
import DirectLinkFormPreview from "./DirectLinkFormPreview";

export type TAppearance = {
  launcherStyle: "classic" | "minimalButton";
  launcherText: string;
  textContent: string;
  fontFamily: string;
  ctaPosition: "above" | "side";
  ctaPositionDesktop: "right" | "left";
  ctaPositionMobile: "right" | "left";
  horizontalOffsetDesktop: string;
  horizontalOffsetMobile: string;
  isAutoPlayDesktop: "true" | "false";
  isAutoPlayMobile: "true" | "false";
  isCtaOpenByDefaultDesktop: "true" | "false";
  isCtaOpenByDefaultMobile: "true" | "false";
  textColor: string;
  themeColor: string;
  verticalOffsetDesktop: string;
  verticalOffsetMobile: string;
  videoPlayFrequency: string;
  mobileOrDesktopView: "Desktop" | "Mobile";
};

type AppearanceTabProps = {
  appearance: TAppearance;
  shouldRenderPreview: boolean;
  highlightedStepId?: TDeployWidgetOnboardingStepId;
  onContinue: () => void;
  onBack: () => void;
  onUpdateAppearance: (field: keyof TAppearance, value: string) => void;
  isFetchingWidgetSettings: boolean;
  areWidgetSettingsReady: boolean;
  continueLabel?: string;
  backLabel?: string;
  hideBackButton?: boolean;
  previewType?: "widget" | "form";
  formPreviewUrl?: string;
};

const normalizePreviewSiteUrl = (value: string): string => {
  const trimmedValue = value.trim();
  if (!trimmedValue) return "";
  const candidateUrl = /^https?:\/\//i.test(trimmedValue)
    ? trimmedValue
    : `https://${trimmedValue}`;
  try {
    const parsedUrl = new URL(candidateUrl);
    if (parsedUrl.protocol !== "https:" && parsedUrl.protocol !== "http:") {
      return "";
    }
    return parsedUrl.toString();
  } catch {
    return "";
  }
};

const escapeHtmlAttribute = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const APPEARANCE_LABEL_WIDTH = 160;
const APPEARANCE_LONG_LABEL_WIDTH = 190;
const DEPLOY_WIDGET_TOOLTIP_EVENT = "Deploy Widget Tooltip Opened";
const DEFAULT_CLASSIC_THEME_COLORS = new Set(["#00b7f4", "#00b4f4"]);
const DEFAULT_MINIMAL_BUTTON_COLOR = "#0c0c0c";
const APPEARANCE_AUTOSAVE_DELAY_MS = 600;

type TPersistedAppearance = Omit<TAppearance, "mobileOrDesktopView">;

const getPersistedAppearance = (
  appearance: TAppearance,
): TPersistedAppearance => {
  const { mobileOrDesktopView: _previewDevice, ...widgetSettings } = appearance;
  return widgetSettings;
};

const serializePersistedAppearance = (appearance: TAppearance): string =>
  JSON.stringify(getPersistedAppearance(appearance));

const AppearanceTab: React.FC<AppearanceTabProps> = ({
  appearance,
  shouldRenderPreview,
  highlightedStepId,
  onContinue,
  onBack,
  onUpdateAppearance,
  isFetchingWidgetSettings,
  areWidgetSettingsReady,
  continueLabel = "Next: Deploy",
  backLabel = "Back: Profile",
  hideBackButton = false,
  previewType = "widget",
  formPreviewUrl = "",
}) => {
  const session = React.useContext(SessionContext)?.session;

  const [iframeSrc, setIframeSrc] = React.useState<string>("");
  const [previewSiteUrl, setPreviewSiteUrl] = React.useState<string>("");
  const hasInitializedPreviewSiteUrlRef = React.useRef(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>("");
  const latestAppearanceRef = React.useRef(appearance);
  const lastSavedAppearanceRef = React.useRef<string | null>(null);
  const hasInitializedAutosaveRef = React.useRef(false);
  const autosaveTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const savePromiseRef = React.useRef<Promise<boolean> | null>(null);
  const [isAdvancedCustomizationOpen, setIsAdvancedCustomizationOpen] =
    React.useState(false);
  const currentView = appearance?.mobileOrDesktopView ?? "Desktop";
  const isMinimalLauncher = appearance?.launcherStyle === "minimalButton";
  const isFormPreview = previewType === "form";
  const normalizedPreviewSiteUrl = React.useMemo(
    () => normalizePreviewSiteUrl(previewSiteUrl),
    [previewSiteUrl],
  );
  const hasPreviewSiteUrl = previewSiteUrl.trim().length > 0;
  const hasInvalidPreviewSiteUrl =
    hasPreviewSiteUrl && !normalizedPreviewSiteUrl;

  latestAppearanceRef.current = appearance;

  const persistLatestAppearance =
    React.useCallback(async (): Promise<boolean> => {
      if (!areWidgetSettingsReady) return false;
      if (savePromiseRef.current) return savePromiseRef.current;

      const savePromise = (async () => {
        if (!session) {
          setError(
            "Unable to save appearance. Refresh the page and try again.",
          );
          return false;
        }

        setLoading(true);
        setError("");

        try {
          while (true) {
            const nextAppearance = latestAppearanceRef.current;
            const nextSnapshot = serializePersistedAppearance(nextAppearance);

            if (nextSnapshot === lastSavedAppearanceRef.current) break;

            await postClinicProfile(session, {
              widget_settings: getPersistedAppearance(nextAppearance),
            });
            lastSavedAppearanceRef.current = nextSnapshot;
          }
          return true;
        } catch {
          setError(
            "Appearance could not be saved. Change a setting or press Continue to retry.",
          );
          return false;
        } finally {
          setLoading(false);
        }
      })();

      savePromiseRef.current = savePromise;
      const didSave = await savePromise;
      savePromiseRef.current = null;
      return didSave;
    }, [areWidgetSettingsReady, session]);

  React.useEffect(() => {
    if (!areWidgetSettingsReady) {
      hasInitializedAutosaveRef.current = false;
      lastSavedAppearanceRef.current = null;
      return;
    }

    const appearanceSnapshot = serializePersistedAppearance(appearance);
    if (!hasInitializedAutosaveRef.current) {
      hasInitializedAutosaveRef.current = true;
      lastSavedAppearanceRef.current = appearanceSnapshot;
      return;
    }

    if (appearanceSnapshot === lastSavedAppearanceRef.current) return;

    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
    }
    autosaveTimeoutRef.current = setTimeout(() => {
      autosaveTimeoutRef.current = null;
      void persistLatestAppearance();
    }, APPEARANCE_AUTOSAVE_DELAY_MS);

    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
        autosaveTimeoutRef.current = null;
      }
    };
  }, [appearance, areWidgetSettingsReady, persistLatestAppearance]);

  React.useEffect(() => {
    if (isFormPreview) return;
    if (hasInitializedPreviewSiteUrlRef.current) return;

    const queryParams = new URLSearchParams(window.location.search);
    const websiteFromQuery = queryParams.get("website")?.trim() || "";
    const websiteFromSession = session?.websiteUrl?.trim() || "";
    const initialWebsiteUrl = websiteFromSession || websiteFromQuery;

    if (!initialWebsiteUrl) return;
    setPreviewSiteUrl(initialWebsiteUrl);
    hasInitializedPreviewSiteUrlRef.current = true;
  }, [isFormPreview, session?.websiteUrl]);

  const handleToggleView = React.useCallback(
    (view: "Desktop" | "Mobile") => {
      if (currentView === view) return;
      onUpdateAppearance("mobileOrDesktopView", view);
    },
    [currentView, onUpdateAppearance],
  );

  const handleSelectLauncherStyle = React.useCallback(
    (launcherStyle: TAppearance["launcherStyle"]) => {
      onUpdateAppearance("launcherStyle", launcherStyle);

      if (
        launcherStyle === "minimalButton" &&
        DEFAULT_CLASSIC_THEME_COLORS.has(appearance.themeColor.toLowerCase())
      ) {
        onUpdateAppearance("themeColor", DEFAULT_MINIMAL_BUTTON_COLOR);
      }
    },
    [appearance.themeColor, onUpdateAppearance],
  );

  React.useEffect(() => {
    if (!appearance) return;
    if (isFormPreview) {
      setIframeSrc("");
      return;
    }
    const clinicSdkKey = session?.clinicSdkKeys?.[0];
    if (!clinicSdkKey) return;
    const widgetScriptSrc =
      process.env.EXPO_PUBLIC_ENV === "production"
        ? "https://www.clinic-os.com/clinicos-full-form-get-started-tab-user-app.js"
        : "https://www.clinic-os.com/clinicos-full-form-get-started-tab-user-app-dev.js";
    const encodedTextContent = encodeURIComponent(appearance.textContent || "");
    const encodedLauncherText = encodeURIComponent(
      appearance.launcherText || "Visualize with AI",
    );
    const escapedPreviewSiteUrl = escapeHtmlAttribute(normalizedPreviewSiteUrl);
    const previewBackgroundMarkup = normalizedPreviewSiteUrl
      ? `<iframe
      class="preview-background-frame"
      src="${escapedPreviewSiteUrl}"
      loading="lazy"
      referrerpolicy="no-referrer-when-downgrade"
    ></iframe>`
      : "";

    setIframeSrc(`<!doctype html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <style>
      html, body {
        margin: 0;
        padding: 0;
        width: 100%;
        height: 100%;
        font-family: Urbanist, Arial, sans-serif;
        background: linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%);
      }
      .preview-background-frame {
        position: fixed;
        inset: 0;
        width: 100%;
        height: 100%;
        border: 0;
        background: #ffffff;
        z-index: 0;
      }
      .mock-page {
        position: relative;
        z-index: 1;
        min-height: 100%;
      }
      /* Preview-only CTA bubble treatment so widget contrast matches real sites. */
      #clinicos-flow__modal-select {
        background: #ffffff !important;
        border: 1px solid #d0d7e2 !important;
        border-radius: 15px !important;
        box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12) !important;
      }
    </style>
  </head>
  <body>
    ${previewBackgroundMarkup}
    <div class="mock-page"></div>
    <script
      src="${widgetScriptSrc}"
      clinicSdkKey="${clinicSdkKey}"
      formName="onboarding-tier1"
      launcherStyle="${appearance.launcherStyle}"
      launcherText="${encodedLauncherText}"
      textContent="${encodedTextContent}"
      fontFamily="${appearance.fontFamily}"
      themeColor="${appearance.themeColor}"
      textColor="${appearance.textColor}"
      videoPlayFrequency="${appearance.videoPlayFrequency}"
      ctaPosition="${appearance.ctaPosition}"
      ctaPositionMobile="${appearance.ctaPositionMobile}"
      ctaPositionDesktop="${appearance.ctaPositionDesktop}"
      isCtaOpenByDefaultMobile="${appearance.isCtaOpenByDefaultMobile}"
      isCtaOpenByDefaultDesktop="${appearance.isCtaOpenByDefaultDesktop}"
      horizontalOffsetDesktop="${appearance.horizontalOffsetDesktop}px"
      verticalOffsetDesktop="${appearance.verticalOffsetDesktop}px"
      horizontalOffsetMobile="${appearance.horizontalOffsetMobile}px"
      verticalOffsetMobile="${appearance.verticalOffsetMobile}px"
      isVideoPlayMobile="${appearance.isAutoPlayMobile}"
      isVideoPlayDesktop="${appearance.isAutoPlayDesktop}"
    ></script>
  </body>
</html>`);
  }, [
    appearance,
    isFormPreview,
    normalizedPreviewSiteUrl,
    session?.clinicSdkKeys,
  ]);

  const handleOnClickContinue = async () => {
    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
      autosaveTimeoutRef.current = null;
    }

    const didSave = await persistLatestAppearance();
    if (didSave) {
      onContinue();
    }
  };

  if (!appearance) return null;
  const isDesktopView = currentView === "Desktop";
  const desktopPreviewAspectRatio = 4 / 3;

  return (
    <View style={{ flex: 1, gap: 10, marginTop: 10 }}>
      <View
        style={{
          flexDirection: "row",
          alignSelf: "flex-start",
          gap: 4,
          alignItems: "center",
        }}
      >
        <RainbowBorder
          isActive={highlightedStepId === "appearance-device-settings"}
          borderRadius={999}
        >
          <View
            style={{
              flexDirection: "row",
              alignSelf: "flex-start",
              backgroundColor: "#F2F4F7",
              borderRadius: 999,
              padding: 4,
              gap: 4,
            }}
          >
            {(["Desktop", "Mobile"] as const).map((option) => {
              const isSelected = currentView === option;
              return (
                <Pressable
                  key={option}
                  onPress={() => handleToggleView(option)}
                  style={{
                    paddingVertical: 8,
                    paddingHorizontal: 16,
                    borderRadius: 999,
                    backgroundColor: isSelected ? "#00B7F4" : "transparent",
                  }}
                >
                  <NormalText
                    text={option}
                    style={{
                      color: isSelected ? "#FFFFFF" : "#000000",
                    }}
                    fontWeight={isSelected ? "600" : "500"}
                  />
                </Pressable>
              );
            })}
          </View>
        </RainbowBorder>
        <InfoTooltipButton
          text={DEPLOY_WIDGET_TOOLTIPS.appearance.previewDevice}
          accessibilityLabel="Show help for preview device settings"
          trackingEventName={DEPLOY_WIDGET_TOOLTIP_EVENT}
          trackingProperties={{
            Tab: "Appearance",
            "Tooltip Name": "Preview Device",
          }}
        />
      </View>
      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10 }}>
        <View
          style={{ flex: 1, width: "100%", minWidth: 350, gap: 10, zIndex: 1 }}
        >
          <NormalText
            text={
              isFormPreview
                ? "Customize Form Appearance"
                : "Customize Widget Appearance"
            }
            fontWeight="600"
          />
          <NormalText
            text={
              isFormPreview
                ? "Adjust the form styling and review the direct-link experience in the preview pane"
                : "Adjust the widget settings and view the widget in the preview pane"
            }
          />

          <View style={{ width: "100%", maxWidth: 400, gap: 10 }}>
            {!isFormPreview ? (
              <>
                <View style={{ gap: 6 }}>
                  <NormalText text="Launcher Style" fontWeight="600" />
                  <View
                    style={{
                      flexDirection: "row",
                      backgroundColor: "#F2F4F7",
                      borderRadius: 999,
                      padding: 4,
                      gap: 4,
                    }}
                  >
                    {(
                      [
                        { label: "Classic", value: "classic" },
                        { label: "Minimal Button", value: "minimalButton" },
                      ] as const
                    ).map((option) => {
                      const isSelected =
                        appearance.launcherStyle === option.value;
                      return (
                        <Pressable
                          key={option.value}
                          onPress={() =>
                            handleSelectLauncherStyle(option.value)
                          }
                          style={{
                            flex: 1,
                            minHeight: 40,
                            paddingVertical: 8,
                            paddingHorizontal: 12,
                            borderRadius: 999,
                            backgroundColor: isSelected
                              ? "#00B7F4"
                              : "transparent",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <NormalText
                            text={option.label}
                            style={{
                              color: isSelected ? "#FFFFFF" : "#000000",
                              textAlign: "center",
                            }}
                            fontWeight={isSelected ? "600" : "500"}
                          />
                        </Pressable>
                      );
                    })}
                  </View>
                </View>
                {isMinimalLauncher ? (
                  <InputPatientDetail
                    name="Launcher Text"
                    textWidth={APPEARANCE_LABEL_WIDTH}
                    maxLength={80}
                    value={appearance.launcherText}
                    onChangeText={(value) =>
                      onUpdateAppearance("launcherText", value)
                    }
                  />
                ) : (
                  <InputPatientDetail
                    name="Text Content"
                    textWidth={APPEARANCE_LABEL_WIDTH}
                    inputHeight={120}
                    multiline={true}
                    numberOfLines={5}
                    maxLength={500}
                    value={appearance.textContent}
                    tooltipText={DEPLOY_WIDGET_TOOLTIPS.appearance.textContent}
                    tooltipTrackingEventName={DEPLOY_WIDGET_TOOLTIP_EVENT}
                    tooltipTrackingProperties={{ Tab: "Appearance" }}
                    onChangeText={(value) =>
                      onUpdateAppearance("textContent", value)
                    }
                  />
                )}
              </>
            ) : null}
            <View style={{ zIndex: 1 }}>
              <InputPatientDetailDropdown
                name={"Font Family"}
                textWidth={APPEARANCE_LABEL_WIDTH}
                value={[appearance.fontFamily]}
                selections={[
                  "Arial",
                  "Trebuchet MS",
                  "Times New Roman",
                  "Garamond",
                  "Courier New",
                  "Brush Script MT",
                  "Urbanist",
                  "Montserrat",
                ]}
                tooltipText={DEPLOY_WIDGET_TOOLTIPS.appearance.fontFamily}
                tooltipTrackingEventName={DEPLOY_WIDGET_TOOLTIP_EVENT}
                tooltipTrackingProperties={{ Tab: "Appearance" }}
                onSelect={(value) => onUpdateAppearance("fontFamily", value[0])}
              />
            </View>
            <InputPatientDetailColorPicker
              name={
                isFormPreview || !isMinimalLauncher
                  ? "Theme Color"
                  : "Button Color"
              }
              textWidth={APPEARANCE_LABEL_WIDTH}
              tooltipText={DEPLOY_WIDGET_TOOLTIPS.appearance.themeColor}
              tooltipTrackingEventName={DEPLOY_WIDGET_TOOLTIP_EVENT}
              tooltipTrackingProperties={{ Tab: "Appearance" }}
              onChange={(value) => onUpdateAppearance("themeColor", value)}
              initialValue={appearance.themeColor}
            />
            <InputPatientDetailColorPicker
              name="Text Color"
              textWidth={APPEARANCE_LABEL_WIDTH}
              tooltipText={DEPLOY_WIDGET_TOOLTIPS.appearance.textColor}
              tooltipTrackingEventName={DEPLOY_WIDGET_TOOLTIP_EVENT}
              tooltipTrackingProperties={{ Tab: "Appearance" }}
              onChange={(value) => onUpdateAppearance("textColor", value)}
              initialValue={appearance.textColor}
            />
            {!isFormPreview && isMinimalLauncher ? (
              <>
                <NormalText
                  text="Placement"
                  fontWeight="600"
                  style={{ marginTop: 4 }}
                />
                {isDesktopView ? (
                  <>
                    <InputPatientDetail
                      name="X offset (px)"
                      textWidth={APPEARANCE_LABEL_WIDTH}
                      value={appearance.horizontalOffsetDesktop}
                      tooltipText={
                        DEPLOY_WIDGET_TOOLTIPS.appearance.horizontalOffset
                      }
                      tooltipTrackingEventName={DEPLOY_WIDGET_TOOLTIP_EVENT}
                      tooltipTrackingProperties={{ Tab: "Appearance" }}
                      onChangeText={(value) =>
                        onUpdateAppearance("horizontalOffsetDesktop", value)
                      }
                    />
                    <InputPatientDetail
                      name="Y offset (px)"
                      textWidth={APPEARANCE_LABEL_WIDTH}
                      value={appearance.verticalOffsetDesktop}
                      tooltipText={
                        DEPLOY_WIDGET_TOOLTIPS.appearance.verticalOffset
                      }
                      tooltipTrackingEventName={DEPLOY_WIDGET_TOOLTIP_EVENT}
                      tooltipTrackingProperties={{ Tab: "Appearance" }}
                      onChangeText={(value) =>
                        onUpdateAppearance("verticalOffsetDesktop", value)
                      }
                    />
                    <View style={{ zIndex: 4 }}>
                      <InputPatientDetailDropdown
                        name="Button Position"
                        textWidth={APPEARANCE_LABEL_WIDTH}
                        value={[appearance.ctaPositionDesktop]}
                        selections={["right", "left"]}
                        tooltipText={
                          DEPLOY_WIDGET_TOOLTIPS.appearance.widgetPosition
                        }
                        tooltipTrackingEventName={DEPLOY_WIDGET_TOOLTIP_EVENT}
                        tooltipTrackingProperties={{ Tab: "Appearance" }}
                        onSelect={(value) =>
                          onUpdateAppearance("ctaPositionDesktop", value[0])
                        }
                      />
                    </View>
                  </>
                ) : (
                  <>
                    <InputPatientDetail
                      name="X offset (px)"
                      textWidth={APPEARANCE_LABEL_WIDTH}
                      value={appearance.horizontalOffsetMobile}
                      tooltipText={
                        DEPLOY_WIDGET_TOOLTIPS.appearance.horizontalOffset
                      }
                      tooltipTrackingEventName={DEPLOY_WIDGET_TOOLTIP_EVENT}
                      tooltipTrackingProperties={{ Tab: "Appearance" }}
                      onChangeText={(value) =>
                        onUpdateAppearance("horizontalOffsetMobile", value)
                      }
                    />
                    <InputPatientDetail
                      name="Y offset (px)"
                      textWidth={APPEARANCE_LABEL_WIDTH}
                      value={appearance.verticalOffsetMobile}
                      tooltipText={
                        DEPLOY_WIDGET_TOOLTIPS.appearance.verticalOffset
                      }
                      tooltipTrackingEventName={DEPLOY_WIDGET_TOOLTIP_EVENT}
                      tooltipTrackingProperties={{ Tab: "Appearance" }}
                      onChangeText={(value) =>
                        onUpdateAppearance("verticalOffsetMobile", value)
                      }
                    />
                    <View style={{ zIndex: 4 }}>
                      <InputPatientDetailDropdown
                        name="Button Position"
                        textWidth={APPEARANCE_LABEL_WIDTH}
                        value={[appearance.ctaPositionMobile]}
                        selections={["right", "left"]}
                        tooltipText={
                          DEPLOY_WIDGET_TOOLTIPS.appearance.widgetPosition
                        }
                        tooltipTrackingEventName={DEPLOY_WIDGET_TOOLTIP_EVENT}
                        tooltipTrackingProperties={{ Tab: "Appearance" }}
                        onSelect={(value) =>
                          onUpdateAppearance("ctaPositionMobile", value[0])
                        }
                      />
                    </View>
                  </>
                )}
              </>
            ) : !isFormPreview ? (
              <>
                <InputPatientDetail
                  name="Video Play Frequency (s)"
                  textWidth={APPEARANCE_LONG_LABEL_WIDTH}
                  value={appearance.videoPlayFrequency}
                  tooltipText={
                    DEPLOY_WIDGET_TOOLTIPS.appearance.videoPlayFrequency
                  }
                  tooltipTrackingEventName={DEPLOY_WIDGET_TOOLTIP_EVENT}
                  tooltipTrackingProperties={{ Tab: "Appearance" }}
                  onChangeText={(value) =>
                    onUpdateAppearance("videoPlayFrequency", value)
                  }
                />
                <Pressable
                  onPress={() =>
                    setIsAdvancedCustomizationOpen((previous) => !previous)
                  }
                  accessibilityRole="button"
                  accessibilityState={{ expanded: isAdvancedCustomizationOpen }}
                  style={{
                    marginTop: 10,
                    minHeight: 46,
                    paddingHorizontal: 12,
                    borderRadius: 10,
                    borderWidth: 1,
                    borderColor: isAdvancedCustomizationOpen
                      ? "#085CCC"
                      : "#C4C6CD",
                    backgroundColor: "#FFFFFF",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <NormalText
                    text="Advanced Customization Options"
                    fontWeight="600"
                    style={{ color: "#282B33" }}
                  />
                  <View
                    style={{
                      transform: [
                        {
                          rotate: isAdvancedCustomizationOpen
                            ? "180deg"
                            : "0deg",
                        },
                      ],
                    }}
                  >
                    <CaretDown size={20} />
                  </View>
                </Pressable>
                {isAdvancedCustomizationOpen ? (
                  <>
                    <NormalText
                      text="View Settings"
                      fontWeight="600"
                      style={{ marginTop: 4 }}
                    />
                    {isDesktopView ? (
                      <>
                        <InputPatientDetail
                          name="X offset (px)"
                          textWidth={APPEARANCE_LABEL_WIDTH}
                          value={appearance.horizontalOffsetDesktop}
                          tooltipText={
                            DEPLOY_WIDGET_TOOLTIPS.appearance.horizontalOffset
                          }
                          tooltipTrackingEventName={DEPLOY_WIDGET_TOOLTIP_EVENT}
                          tooltipTrackingProperties={{ Tab: "Appearance" }}
                          onChangeText={(value) =>
                            onUpdateAppearance("horizontalOffsetDesktop", value)
                          }
                        />
                        <InputPatientDetail
                          name="Y offset (px)"
                          textWidth={APPEARANCE_LABEL_WIDTH}
                          value={appearance.verticalOffsetDesktop}
                          tooltipText={
                            DEPLOY_WIDGET_TOOLTIPS.appearance.verticalOffset
                          }
                          tooltipTrackingEventName={DEPLOY_WIDGET_TOOLTIP_EVENT}
                          tooltipTrackingProperties={{ Tab: "Appearance" }}
                          onChangeText={(value) =>
                            onUpdateAppearance("verticalOffsetDesktop", value)
                          }
                        />
                        <View style={{ zIndex: 6 }}>
                          <InputPatientDetailDropdown
                            name="Auto Play Video"
                            textWidth={APPEARANCE_LABEL_WIDTH}
                            value={[appearance.isAutoPlayDesktop]}
                            selections={["true", "false"]}
                            tooltipText={
                              DEPLOY_WIDGET_TOOLTIPS.appearance.autoPlayVideo
                            }
                            tooltipTrackingEventName={
                              DEPLOY_WIDGET_TOOLTIP_EVENT
                            }
                            tooltipTrackingProperties={{ Tab: "Appearance" }}
                            onSelect={(value) =>
                              onUpdateAppearance("isAutoPlayDesktop", value[0])
                            }
                          />
                        </View>
                        <View style={{ zIndex: 5 }}>
                          <InputPatientDetailDropdown
                            name="Show CTA"
                            textWidth={APPEARANCE_LABEL_WIDTH}
                            value={[appearance.isCtaOpenByDefaultDesktop]}
                            selections={["true", "false"]}
                            tooltipText={
                              DEPLOY_WIDGET_TOOLTIPS.appearance.showCta
                            }
                            tooltipTrackingEventName={
                              DEPLOY_WIDGET_TOOLTIP_EVENT
                            }
                            tooltipTrackingProperties={{ Tab: "Appearance" }}
                            onSelect={(value) =>
                              onUpdateAppearance(
                                "isCtaOpenByDefaultDesktop",
                                value[0],
                              )
                            }
                          />
                        </View>
                        <View style={{ zIndex: 4 }}>
                          <InputPatientDetailDropdown
                            name="Widget Position"
                            textWidth={APPEARANCE_LABEL_WIDTH}
                            value={[appearance.ctaPositionDesktop]}
                            selections={["right", "left"]}
                            tooltipText={
                              DEPLOY_WIDGET_TOOLTIPS.appearance.widgetPosition
                            }
                            tooltipTrackingEventName={
                              DEPLOY_WIDGET_TOOLTIP_EVENT
                            }
                            tooltipTrackingProperties={{ Tab: "Appearance" }}
                            onSelect={(value) =>
                              onUpdateAppearance("ctaPositionDesktop", value[0])
                            }
                          />
                        </View>
                      </>
                    ) : (
                      <>
                        <InputPatientDetail
                          name="X offset (px)"
                          textWidth={APPEARANCE_LABEL_WIDTH}
                          value={appearance.horizontalOffsetMobile}
                          tooltipText={
                            DEPLOY_WIDGET_TOOLTIPS.appearance.horizontalOffset
                          }
                          tooltipTrackingEventName={DEPLOY_WIDGET_TOOLTIP_EVENT}
                          tooltipTrackingProperties={{ Tab: "Appearance" }}
                          onChangeText={(value) =>
                            onUpdateAppearance("horizontalOffsetMobile", value)
                          }
                        />
                        <InputPatientDetail
                          name="Y offset (px)"
                          textWidth={APPEARANCE_LABEL_WIDTH}
                          value={appearance.verticalOffsetMobile}
                          tooltipText={
                            DEPLOY_WIDGET_TOOLTIPS.appearance.verticalOffset
                          }
                          tooltipTrackingEventName={DEPLOY_WIDGET_TOOLTIP_EVENT}
                          tooltipTrackingProperties={{ Tab: "Appearance" }}
                          onChangeText={(value) =>
                            onUpdateAppearance("verticalOffsetMobile", value)
                          }
                        />
                        <View style={{ zIndex: 6 }}>
                          <InputPatientDetailDropdown
                            name="Auto Play Video"
                            textWidth={APPEARANCE_LABEL_WIDTH}
                            value={[appearance.isAutoPlayMobile]}
                            selections={["true", "false"]}
                            tooltipText={
                              DEPLOY_WIDGET_TOOLTIPS.appearance.autoPlayVideo
                            }
                            tooltipTrackingEventName={
                              DEPLOY_WIDGET_TOOLTIP_EVENT
                            }
                            tooltipTrackingProperties={{ Tab: "Appearance" }}
                            onSelect={(value) =>
                              onUpdateAppearance("isAutoPlayMobile", value[0])
                            }
                          />
                        </View>
                        <View style={{ zIndex: 5 }}>
                          <InputPatientDetailDropdown
                            name="Show CTA"
                            textWidth={APPEARANCE_LABEL_WIDTH}
                            value={[appearance.isCtaOpenByDefaultMobile]}
                            selections={["true", "false"]}
                            tooltipText={
                              DEPLOY_WIDGET_TOOLTIPS.appearance.showCta
                            }
                            tooltipTrackingEventName={
                              DEPLOY_WIDGET_TOOLTIP_EVENT
                            }
                            tooltipTrackingProperties={{ Tab: "Appearance" }}
                            onSelect={(value) =>
                              onUpdateAppearance(
                                "isCtaOpenByDefaultMobile",
                                value[0],
                              )
                            }
                          />
                        </View>
                        <View style={{ zIndex: 4 }}>
                          <InputPatientDetailDropdown
                            name="Widget Position"
                            textWidth={APPEARANCE_LABEL_WIDTH}
                            value={[appearance.ctaPositionMobile]}
                            selections={["right", "left"]}
                            tooltipText={
                              DEPLOY_WIDGET_TOOLTIPS.appearance.widgetPosition
                            }
                            tooltipTrackingEventName={
                              DEPLOY_WIDGET_TOOLTIP_EVENT
                            }
                            tooltipTrackingProperties={{ Tab: "Appearance" }}
                            onSelect={(value) =>
                              onUpdateAppearance("ctaPositionMobile", value[0])
                            }
                          />
                        </View>
                      </>
                    )}
                    <View style={{ zIndex: 3 }}>
                      <InputPatientDetailDropdown
                        name="CTA Position"
                        textWidth={APPEARANCE_LABEL_WIDTH}
                        value={[appearance.ctaPosition]}
                        selections={["above", "side"]}
                        tooltipText={
                          DEPLOY_WIDGET_TOOLTIPS.appearance.ctaPosition
                        }
                        tooltipTrackingEventName={DEPLOY_WIDGET_TOOLTIP_EVENT}
                        tooltipTrackingProperties={{ Tab: "Appearance" }}
                        onSelect={(value) =>
                          onUpdateAppearance("ctaPosition", value[0])
                        }
                      />
                    </View>
                  </>
                ) : null}
              </>
            ) : null}
          </View>
        </View>
        <View
          style={{
            flex: 1,
            width: "100%",
            minWidth: 350,
            gap: 10,
          }}
        >
          {isFormPreview ? (
            <>
              <NormalText text="Form Preview" fontWeight="600" />
              <NormalText text="This is what patients see when they open your lead capture link." />
              <DirectLinkFormPreview
                currentView={currentView}
                formPreviewUrl={formPreviewUrl}
                isFetchingSettings={isFetchingWidgetSettings}
                shouldRender={shouldRenderPreview}
              />
            </>
          ) : (
            <>
              <NormalText text={"Widget Preview"} fontWeight="600" />
              <InputPatientDetail
                name="Input your clinic's URL:"
                textWidth={230}
                containerStyle={{ justifyContent: "flex-start" }}
                value={previewSiteUrl}
                placeholder="https://your-clinic-site.com/page"
                autoCapitalize="none"
                autoCorrect={false}
                tooltipText={DEPLOY_WIDGET_TOOLTIPS.appearance.previewSiteUrl}
                tooltipTrackingEventName={DEPLOY_WIDGET_TOOLTIP_EVENT}
                tooltipTrackingProperties={{ Tab: "Appearance" }}
                onChangeText={(value) => setPreviewSiteUrl(value)}
              />
              {hasInvalidPreviewSiteUrl ? (
                <NormalText
                  text="Enter a valid http(s) URL. Example: https://example.com"
                  style={{ color: "#B42318" }}
                />
              ) : null}
              {/* WARNING: Be careful about change this <View> bellow.
              It might cause the responsive on mobile and desktop to be mess up */}
              <RainbowBorder
                isActive={highlightedStepId === "appearance-live-preview"}
                style={{
                  width: "100%",
                  maxWidth: currentView === "Desktop" ? 700 : 410,
                  alignSelf: "flex-start",
                }}
              >
                <View
                  style={{
                    position: "relative",
                    aspectRatio:
                      currentView === "Desktop"
                        ? desktopPreviewAspectRatio
                        : 9 / 16,
                    width: "100%",
                    maxWidth: currentView === "Desktop" ? 700 : 410,
                    borderRadius: 10,
                    overflow: "hidden",
                    marginTop: 10,
                  }}
                >
                  {shouldRenderPreview &&
                  iframeSrc &&
                  !isFetchingWidgetSettings ? (
                    <iframe
                      srcDoc={iframeSrc}
                      allow="autoplay"
                      allowFullScreen
                      sandbox="allow-forms allow-same-origin allow-scripts"
                      style={{
                        position: "absolute",
                        border: 0,
                        aspectRatio:
                          currentView === "Desktop"
                            ? desktopPreviewAspectRatio
                            : 9 / 16,
                        width: currentView === "Desktop" ? "200%" : "100%",
                        maxWidth: currentView === "Desktop" ? 1400 : 410,
                        transformOrigin:
                          currentView === "Desktop" ? "0 0" : "center 0",
                        transform:
                          currentView === "Desktop" ? "scale(0.5)" : "scale(1)",
                        pointerEvents: "none",
                      }}
                      scrolling="no"
                    >
                      Browser doesn't support iframe
                    </iframe>
                  ) : null}
                </View>
              </RainbowBorder>
            </>
          )}
          {/*****************************************************************/}
          <NormalText text={error} style={{ color: "red" }} />
        </View>
      </View>
      <View
        style={{
          marginTop: "auto",
          justifyContent: "center",
          alignContent: "center",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        {!hideBackButton ? (
          <SecondaryButton
            text={backLabel}
            roundBorderRadius
            width={275}
            onPress={() => onBack()}
          />
        ) : null}
        <PrimaryButton
          text={continueLabel}
          roundBorderRadius
          width={275}
          loading={loading}
          disabled={!areWidgetSettingsReady}
          onPress={() => handleOnClickContinue()}
        />
      </View>
    </View>
  );
};

export default AppearanceTab;
